import { Component, ErrorInfo } from "react";
import * as Sentry from "@sentry/react";
import {
  Button,
  Center,
  Group,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import i18n from "utils/i18n/i18n";

type ErrorBoundaryProps = React.PropsWithChildren<void>;

type ErrorBoundaryState = {
  crashReportText: string;
  crashReportSent: boolean;
  errorMeta?: { error: Error; info: ErrorInfo };
  errorCaught: boolean;
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    crashReportText: "",
    crashReportSent: false,
    errorMeta: undefined,
    errorCaught: false,
  };

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ errorCaught: true, errorMeta: { error, info } });
  }

  sendCrashReport = () => {
    this.sendSentryLog();
    this.setState({ crashReportSent: true });
  };

  sendSentryLog = () => {
    if (this.state.errorMeta) {
      const report = this.state.crashReportText ?? "No user report added.";

      Sentry.captureReactException(
        this.state.errorMeta.error,
        this.state.errorMeta.info,
        {
          contexts: {
            "bug-report": { report },
          },
          extra: {
            bugReport: report,
          },
        }
      );
    }
  };

  render() {
    return this.state.errorCaught ? (
      <Center h="100vh">
        <Stack justify="center" h="100%" w={600} gap="lg" px={30}>
          <Title sx={{ textAlign: "center", marginBottom: "10px" }}>
            {i18n.t("error_boundary.title")} 🙄
          </Title>
          <Text sx={{ textAlign: "justify" }}>
            {i18n.t("error_boundary.subtitle")}
          </Text>
          <Text sx={{ textAlign: "justify" }}>
            {i18n.t("error_boundary.main_paragraph")}
          </Text>
          <Stack gap="xs">
            <Textarea
              disabled={this.state.crashReportSent}
              maxLength={500}
              value={this.state.crashReportText}
              onChange={(event) =>
                this.setState({ crashReportText: event.currentTarget.value })
              }
              description={i18n.t("error_boundary.textarea_description")}
              placeholder={i18n.t("error_boundary.textarea_placeholder")}
              resize="vertical"
              w="100%"
              styles={{
                input: {
                  minHeight: "150px",
                },
              }}
            />
            {this.state.crashReportSent ? (
              <Text c="green">{i18n.t("error_boundary.success_message")}</Text>
            ) : null}
          </Stack>
          <Group w="100%" justify="flex-end">
            <Button
              onClick={() => {
                if (!this.state.crashReportSent) {
                  this.sendSentryLog();
                }
                location.reload();
              }}
              w={130}
              variant="filled"
              color="gray"
            >
              {i18n.t("error_boundary.go_back_button_text")}
            </Button>
            <Button
              disabled={this.state.crashReportSent}
              onClick={() => {
                this.sendCrashReport();
              }}
              w={130}
              variant="filled"
            >
              {i18n.t("error_boundary.send_button_text")}
            </Button>
          </Group>
        </Stack>
      </Center>
    ) : (
      <>{this.props.children}</>
    );
  }
}
